import { ReactNode } from 'react';

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  ListItem,
  Slide,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

type CardItemProps = {
  className?: string;
  disabled?: boolean;
  children: ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  isQuote?: boolean;
  fromMe?: boolean;
  isIncomingTransferIndicator?: boolean;
};

const useStyles = makeStyles(({ palette, spacing }) => ({
  card: ({ isQuote }: CardItemProps) => {
    let backgroundColor = palette.background.paper;
    let hoverBackgroundColor = alpha(palette.primary.main, 0.1);

    if (isQuote) {
      backgroundColor = palette.background.paper;
      hoverBackgroundColor =
        palette.type === 'dark'
          ? alpha(palette.common.white, 0.08)
          : palette.background.default;
    }

    return {
      width: '100%',
      position: 'relative',
      backgroundColor,
      border: '1px solid transparent',
      borderRadius: 8,
      transition: 'all 0.1s ease',
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
        border: isQuote
          ? '1px solid transparent'
          : `1px solid ${alpha(palette.primary.main, 0.2)}`,
        cursor: 'pointer',
      },
    };
  },
  cardSelected: {
    '&&': {
      backgroundColor: alpha(palette.primary.main, 0.1),
      border: `1px solid ${alpha(palette.primary.main, 0.2)}`,
    },
  },
  cardDisabled: {
    '&&': {
      backgroundColor: alpha(palette.action.disabledBackground, 0.05),
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: alpha(palette.action.disabledBackground, 0.05),
        border: '1px solid transparent',
        cursor: 'default',
      },
    },
  },
  cardContent: {
    padding: spacing(1, 1.5),
  },
  leftBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    top: 0,
    background: palette.primary.main,
    width: 4,
  },
  leftBorderTransfer: {
    background: palette.warning.main,
  },
}));

export default function CardItem(props: CardItemProps) {
  const {
    className,
    disabled,
    selected,
    onSelect,
    children,
    isQuote,
    isIncomingTransferIndicator,
    ...rest
  } = props;
  const classes = useStyles(props);

  const showLeftBorder = selected || isIncomingTransferIndicator;

  return (
    <ListItem component="div" disableGutters dense>
      <Card
        {...rest}
        elevation={0}
        className={clsx(className, classes.card, {
          [classes.cardSelected]: selected && !disabled,
          [classes.cardDisabled]: disabled,
        })}
      >
        <CardActionArea component={Box} onClick={onSelect} disabled={disabled}>
          <CardContent className={classes.cardContent}>{children}</CardContent>
        </CardActionArea>
        {!isQuote && (
          <Slide
            direction="right"
            in={showLeftBorder}
            mountOnEnter
            unmountOnExit
          >
            <Box
              className={clsx(classes.leftBorder, {
                [classes.leftBorderTransfer]: isIncomingTransferIndicator,
              })}
            />
          </Slide>
        )}
      </Card>
    </ListItem>
  );
}
